import { getLocalizedPagePath } from '@repo/gatsby-localization-helpers';
import {
  SanityShopifyGlobalProduct,
  SanityProductMediaImage,
} from '@repo/models/gatsby-graphql-types';

const schemaPropertyMap = {
  size: 'https://schema.org/size',
  color: 'https://schema.org/color',
  material: 'https://schema.org/material',
  pattern: 'https://schema.org/pattern',
};

type SanityProductWithShopify = SanityShopifyGlobalProduct & {
  shopifyId?: string;
};

const getProductVariantsSchema = (
  shopifyProduct: SanityProductWithShopify,
  siteUrl,
  locale
) => {
  if (!shopifyProduct) {
    return null;
  }

  const productUrl = `${siteUrl}${getLocalizedPagePath(
    `/product/${shopifyProduct.handle}`,
    locale
  )}`;

  const refurbished = false;

  const priceCurrency =
    shopifyProduct?.priceRangeV2?.minVariantPrice?.currencyCode ?? '';

  const productImage = shopifyProduct?.media
    ?.map((m: SanityProductMediaImage) => m?.image?.src)
    ?.filter(i => i)
    ?.at(0);

  const isValidSchemaPropertyKey = (
    key: string
  ): key is keyof typeof schemaPropertyMap => {
    return key in schemaPropertyMap;
  };

  const allOptionNames = new Set<keyof typeof schemaPropertyMap>();

  const variants = shopifyProduct.variants.map(variant => {
    const optionMap: Partial<Record<keyof typeof schemaPropertyMap, string>> =
      {};

    variant.selectedOptions.forEach(option => {
      const name = option.name.toLowerCase();
      if (isValidSchemaPropertyKey(name)) {
        optionMap[name] = option.value;
        allOptionNames.add(name);
      }
    });

    return {
      '@type': 'Product',
      sku: variant.sku,
      name: shopifyProduct.title,
      color: optionMap.color,
      size: optionMap.size,
      material: optionMap.material,
      pattern: optionMap.pattern,
      image: productImage,
      offers: {
        '@type': 'Offer',
        url: `${productUrl}?sku=${variant.sku}`,
        priceCurrency,
        price: variant.price,
        priceSpecification:
          variant.compareAtPrice > variant.price
            ? {
                '@type': 'UnitPriceSpecification',
                priceCurrency,
                price: variant.compareAtPrice,
                name: 'Regular Price',
              }
            : undefined,
        itemCondition: refurbished
          ? 'https://schema.org/RefurbishedCondition'
          : 'https://schema.org/NewCondition',
        availability: variant.availableForSale
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
        // shippingDetails: { },
        // hasMerchantReturnPolicy: { },
      },
    };
  });

  const variesBy = Array.from(allOptionNames)
    .map(name => schemaPropertyMap[name])
    .filter(Boolean);

  const productDescription =
    shopifyProduct?.descriptionHtml?.replace(/<\/?[^>]+(>|$)/g, '') ?? '';

  return {
    '@context': 'https://schema.org/',
    '@type': 'ProductGroup',
    name: shopifyProduct.title,
    description: productDescription,
    url: productUrl,
    brand: {
      '@type': 'Brand',
      name: shopifyProduct?.vendor,
    },
    productGroupID: shopifyProduct.shopifyId.replace(
      'gid://shopify/Product/',
      ''
    ),
    image: productImage,
    variesBy,
    hasVariant: variants,
  };
};

export default getProductVariantsSchema;
